import { captureException } from '@sentry/vue';
import ClientBuild from '@/services/ClientBuild';
import { FlowModeEnum } from '@/enum/flow_mode';

const BLIZZARD_EVENT_SNAPCHAT_AVATAR_BUILDER_WEB_PERFORMANCE =
  'SNAPCHAT_AVATAR_BUILDER_WEB_PERFORMANCE';
const BLIZZARD_EVENT_BITMOJI_APP_AVATAR_BUILDER_WEB_PERFORMANCE =
  'BITMOJI_APP_AVATAR_BUILDER_WEB_PERFORMANCE';

class Blizzard {
  private blizzardPromise: Promise<BlizzardAnalytics | void> | null = null;
  private avatarBuilderWebPerformance: AvatarBuilderWebPerformanceEvent = {};

  private traitPreviewLoadedTotal = 0;
  private traitPreviewLoadedError = 0;

  private areMetricsSent = false;
  private isUserInteractionRecorded = false;

  initBlizzard(avatarFlow: FlowModeEnum): Promise<BlizzardAnalytics | void> {
    this.avatarBuilderWebPerformance.avatar_flow = avatarFlow;
    return (this.blizzardPromise = import(/* webpackChunkName: "blizzard" */ '@snapchat/blizzard')
      .then((module) => module.default)
      .then((Analytics) => {
        const isProd = BUILD_FLAGS.ENVIRONMENT === 'production';
        Analytics.setCookiesEnabled(false);
        Analytics.setLoggingEnabled(!isProd);
        Analytics.init();
        return Analytics;
      })
      .catch((e) => {
        captureException(e);
      }));
  }

  recordPageLoad(): void {
    try {
      if (PAGE_LOAD_START_TIME_MS && PAGE_LOAD_START_TIME_MS > 0) {
        this.avatarBuilderWebPerformance.page_load_start_time = PAGE_LOAD_START_TIME_MS;
        this.avatarBuilderWebPerformance.page_load_end_time = Date.now();
        this.avatarBuilderWebPerformance.page_load_latency =
          this.avatarBuilderWebPerformance.page_load_end_time -
          this.avatarBuilderWebPerformance.page_load_start_time;
      }
    } catch (e) {
      captureException(e);
    }
  }

  recordFirstMainPreviewLoad(): void {
    try {
      // Only record the first time loading main preview
      if (
        PAGE_LOAD_START_TIME_MS &&
        PAGE_LOAD_START_TIME_MS > 0 &&
        !this.avatarBuilderWebPerformance.first_main_preview_loaded_time
      ) {
        this.avatarBuilderWebPerformance.first_main_preview_loaded_time = Date.now();
        this.avatarBuilderWebPerformance.first_main_preview_visible_latency =
          this.avatarBuilderWebPerformance.first_main_preview_loaded_time - PAGE_LOAD_START_TIME_MS;
      }
    } catch (e) {
      captureException(e);
    }
  }

  recordAssetsRequestLatency(latency: number): void {
    this.avatarBuilderWebPerformance.abv3_assets_load_latency = latency;
  }

  recordFirstUserInteraction(): void {
    try {
      if (
        !this.isUserInteractionRecorded &&
        PAGE_LOAD_START_TIME_MS &&
        PAGE_LOAD_START_TIME_MS > 0
      ) {
        this.avatarBuilderWebPerformance.time_to_interact = Date.now() - PAGE_LOAD_START_TIME_MS;
        this.isUserInteractionRecorded = true;
      }
    } catch (e) {
      captureException(e);
    }
  }

  recordTraitPreviewLoad(error: boolean) {
    this.traitPreviewLoadedTotal++;
    if (error) {
      this.traitPreviewLoadedError++;
    }
  }

  recordExit(exitCategory: string, withSaveExit: boolean): Promise<void> | undefined {
    try {
      this.avatarBuilderWebPerformance.total_trait_preview_loaded = this.traitPreviewLoadedTotal;
      this.avatarBuilderWebPerformance.error_rate_trait_preview =
        this.traitPreviewLoadedError / this.traitPreviewLoadedTotal;
      this.avatarBuilderWebPerformance.exit_category = exitCategory;
      this.avatarBuilderWebPerformance.with_save_exit = withSaveExit;
      if (PAGE_LOAD_START_TIME_MS && PAGE_LOAD_START_TIME_MS > 0) {
        this.avatarBuilderWebPerformance.total_time_spent = Date.now() - PAGE_LOAD_START_TIME_MS;
      }
      return this.sendMetrics();
    } catch (e) {
      captureException(e);
    }
  }

  get eventName(): string {
    return ClientBuild.getClientApp() === ClientBuild.ClientAppEnum.Snapchat
      ? BLIZZARD_EVENT_SNAPCHAT_AVATAR_BUILDER_WEB_PERFORMANCE
      : BLIZZARD_EVENT_BITMOJI_APP_AVATAR_BUILDER_WEB_PERFORMANCE;
  }

  get performanceMetrics(): AvatarBuilderWebPerformanceEvent {
    return this.avatarBuilderWebPerformance;
  }

  // For unit tests
  set performanceMetrics(metrics: AvatarBuilderWebPerformanceEvent) {
    this.avatarBuilderWebPerformance = metrics;
  }

  private validate() {
    try {
      return (
        PAGE_LOAD_START_TIME_MS &&
        PAGE_LOAD_START_TIME_MS > 0 &&
        ClientBuild.getClientApp() !== ClientBuild.ClientAppEnum.Unknown
      );
    } catch (e) {
      captureException(e);
      return false;
    }
  }

  private sendMetrics(): Promise<void> | undefined {
    if (!this.areMetricsSent && this.validate()) {
      return this.blizzardPromise
        ?.then((Analytics) => {
          if (Analytics) {
            Analytics.log(this.eventName, this.avatarBuilderWebPerformance);
          }
          this.areMetricsSent = true;
        })
        .catch((e) => {
          captureException(e);
        });
    }
  }
}

export default new Blizzard();
